import { useCallback, useEffect, useRef, useState } from 'react';

import { generatePrompt } from 'modules/current-compete/libs/ask-klue-research-mode-chat/api/export.api';

type UseAutoGeneratePromptReturn = {
  isGenerating: boolean;
  error: boolean;
  retry: () => void;
  cancel: () => void;
};

export function useAutoGeneratePrompt(
  threadId: string | undefined,
  onPromptChange: (text: string) => void,
): UseAutoGeneratePromptReturn {
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState(false);
  const abortRef = useRef<AbortController | null>(null);
  const onPromptChangeRef = useRef(onPromptChange);
  onPromptChangeRef.current = onPromptChange;

  const generateRef = useRef<() => Promise<void>>();
  generateRef.current = async (): Promise<void> => {
    if (!threadId) return;

    abortRef.current?.abort();
    const abortController = new AbortController();
    abortRef.current = abortController;

    setError(false);
    setIsGenerating(true);
    onPromptChangeRef.current('');

    try {
      const result = await generatePrompt(threadId, abortController.signal);
      onPromptChangeRef.current(result.prompt);
    } catch (err) {
      if (!(err instanceof DOMException && err.name === 'AbortError')) {
        setError(true);
      }
    } finally {
      setIsGenerating(false);
      if (abortRef.current === abortController) {
        abortRef.current = null;
      }
    }
  };

  const retry = useCallback((): void => {
    void generateRef.current?.();
  }, []);

  const cancel = useCallback((): void => {
    abortRef.current?.abort();
    abortRef.current = null;
    setIsGenerating(false);
    setError(false);
  }, []);

  useEffect(() => {
    if (threadId) {
      void generateRef.current?.();
    }

    return () => {
      abortRef.current?.abort();
      abortRef.current = null;
    };
  }, [threadId]);

  return { isGenerating, error, retry, cancel };
}
